import { applyMiddleware, compose, createStore as _createStore, Reducer, Store } from 'redux';
// tslint:disable-next-line: match-default-export-name
import thunk from 'redux-thunk';

import { ERequestApiStatus, TAppState, TSerializableAppState } from '../types/appState';
import { IApplicationContext } from '../types/applicationContext';
import { TReduxActions } from '../types/redux/actions';
import { EventChannel, TSocketState } from '../types/socket';
import { serealizeMap } from '../utils/serealize_map';

interface ICustomReducer extends Reducer<TAppState> {
  addReducer(reducer: Reducer<TAppState>): void;
}

function createCustomReducer(): ICustomReducer {
  const subReducers: Reducer<TAppState>[] = [];

  const reducer = ((state: TAppState, action: TReduxActions) =>
    subReducers.reduce((acc, subReducer) => subReducer(acc, action), state)) as ICustomReducer;

  reducer.addReducer = (subReducer: Reducer<TAppState>) => {
    subReducers.push(subReducer);
  };

  return reducer;
}

export interface IAppStore extends Store<TAppState> {
  addReducer(reducer: Reducer<TAppState>): void;
  serialize(): TSerializableAppState;
}

interface IAppStoreConfig {
  initialState: TSerializableAppState;
  eventChannel?: EventChannel;
  optionalParams?: {
    dispatchReducerAddedAction?: boolean;
    dispatchRunnerSagaAction?: boolean;
  };
  context: IApplicationContext;
}

export function createAppStore({ eventChannel, initialState, optionalParams, context }: IAppStoreConfig): IAppStore {
  const socket: TSocketState = {
    clientEvents: new Map(),
    eventChannel: eventChannel as EventChannel,
    status: ERequestApiStatus.Initial,
  };

  const state = Object.assign({} as TAppState, initialState, { socket });

  const customReducer = createCustomReducer();

  const composeEnhancers =
    typeof window === 'object' && window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__
      ? window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__
      : compose;

  const thunkMiddleware = thunk.withExtraArgument(context);

  const createStore = () => _createStore(customReducer, state, composeEnhancers(applyMiddleware(thunkMiddleware)));

  const store = createStore();

  function addReducer(reducer: Reducer<TAppState>) {
    if (optionalParams && optionalParams.dispatchReducerAddedAction) {
      store.dispatch({
        name: reducer.name,
        type: '@@store/REDUCER_ADDED',
      });
    }
    customReducer.addReducer(reducer);
  }

  function serialize(): TSerializableAppState {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { socket, ...serializableState } = store.getState();

    const currentState = Object.assign({}, serializableState);

    currentState.chatWindow.serealizebleMap = serealizeMap(currentState.chatWindow.messagesMap);

    return currentState;
  }

  return { ...store, addReducer, serialize };
}
